import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'

interface PersonData {
  display_name?: string
  first_name?: string
  last_name?: string
  middle_name?: string
  prefix?: string
  suffix?: string
  full_name?: string
  nickname?: string
  phonetic_first_name?: string
  phonetic_last_name?: string
  phonetic_middle_name?: string
}

interface OrganizationData {
  job_title?: string
  department?: string
  company?: string
  phonetic_company?: string
}

interface PhoneData {
  type?: string
  number?: string
}

interface EmailData {
  type?: string
  email?: string
  address?: string
}

interface AddressData {
  type?: string
  address?: string
}

interface LinkData {
  type?: string
  url?: string
}

interface DateData {
  type?: string
  date?: string
}

interface RelatedPersonData {
  type?: string
  name?: string
}

interface CustomFieldData {
  label?: string
  value?: string
}

interface NoteData {
  id?: string
  type?: string
  text?: string
  content?: string
  creator?: string
  updated_at?: string | number
}

interface CategoryData {
  id?: string | number
  name?: string
  color?: string
}

interface PhotoData {
  url?: string
  file?: File
}

interface SharedContactData {
  person?: PersonData
  organization?: OrganizationData
  categories?: CategoryData[]
  emails?: EmailData[]
  photo?: PhotoData | string
  phones?: PhoneData[]
  addresses?: AddressData[]
  links?: LinkData[]
  event_dates?: DateData[]
  related_persons?: RelatedPersonData[]
  customs?: CustomFieldData[]
  notes?: NoteData[]
  favorite?: boolean
  id?: string
}

interface OrganizationContactData {
  display_name?: string
  email?: string
  emails?: { type?: string; address?: string }[]
  profile_photo_url?: string
  is_use_default_photo?: boolean
  jobData?: {
    jobposition?: string
    jobtitle?: string
  }
  departmentData?: {
    title?: string
  }
  mobiles?: { type?: string; value?: string }[]
  tels?: string[]
}

interface PersonalContactDetails {
  person?: PersonData
  organization?: OrganizationData
  emails?: EmailData[]
  hasPhoto?: boolean
  photo?: string
  phones?: PhoneData[]
  addresses?: AddressData[]
  links?: LinkData[]
  dates?: DateData[]
  relatedPersons?: RelatedPersonData[]
  customFields?: CustomFieldData[]
  notes?: NoteData[]
  uid?: string
}

interface PersonalContactData {
  contact: PersonalContactDetails
  serverId?: string
  etag?: string
}

interface ByEmailData {
  name: string
  email: string
}

interface MergeInfo {
  email?: string
}

class DetailData {
  // 기본 정보
  displayName: string | null = null
  prefix: string | null = null
  suffix: string | null = null
  email: string | null = null
  position: string | null = null
  department: string | null = null
  company: string | null = null
  photoUrl: string | null = null
  orgInfo: string | null = null
  categoryTags: CategoryData[] | null = null
  favorite: boolean = false
  id: string | null = null
  sharedContactData: SharedContactData | null = null
  personalContactData: PersonalContactData | null = null
  yomiFirstName: string | null = null
  yomiLastName: string | null = null
  yomiMiddleName: string | null = null
  yomiCompany: string | null = null

  // 상세 정보
  phones: PhoneData[] = []
  emails: EmailData[] = []
  addresses: AddressData[] = []
  links: LinkData[] = []
  dates: DateData[] = []
  relatedPersons: RelatedPersonData[] = []
  customFields: CustomFieldData[] = []
  notes: NoteData[] = []

  // 이름 관련
  firstName: string | null = null
  lastName: string | null = null
  middleName: string | null = null
  fullName: string | null = null

  // Organization 관련
  isOrganization: boolean = false

  isMerged: boolean = false

  // Personal contact 관련
  isPersonal?: boolean
  nickname?: string | null
  hasPhoto?: boolean
  phone?: string | null
  uid?: string | null
  etag?: string | null

  constructor(
    data?: SharedContactData | OrganizationContactData | PersonalContactData | ByEmailData | null,
    type: 'public' | 'personal' | 'organization' | 'byEmail' | string = 'public',
  ) {
    if (!data) return

    this.isOrganization = type === 'organization'

    if (this.isOrganization) {
      this.initOrganizationData(data as OrganizationContactData)
    } else if (type === 'public') {
      this.initContactData(data as SharedContactData)
    } else if (type === 'personal') {
      this.initPersonalData(data as PersonalContactData)
    } else {
      // byEmail
      const byEmailData = data as ByEmailData
      this.initContactData({
        person: { first_name: byEmailData.name },
        emails: [{ email: byEmailData.email }],
      })
    }
  }

  initContactData(data: SharedContactData): void {
    const { person, organization, categories, emails, photo } = data

    this.sharedContactData = data || null

    // 기본 정보 설정
    this.displayName = person?.display_name || null
    this.email = emails?.[0]?.email || null
    this.position = organization?.job_title || null
    this.department = organization?.department || null
    this.company = organization?.company || null
    this.photoUrl = typeof photo === 'string' ? photo : photo?.url || null
    this.orgInfo = [this.position, this.department, this.company].filter(Boolean).join(' • ')
    this.categoryTags = categories || []

    // 이름 정보 설정
    this.firstName = person?.first_name || null
    this.lastName = person?.last_name || null
    this.middleName = person?.middle_name || null
    this.prefix = person?.prefix || null
    this.suffix = person?.suffix || null
    this.fullName = DetailData.generateFullName(this)
    this.yomiFirstName = person?.phonetic_first_name || null
    this.yomiLastName = person?.phonetic_last_name || null
    this.yomiMiddleName = person?.phonetic_middle_name || null
    this.yomiCompany = organization?.phonetic_company || null

    const { phones, addresses, links, event_dates, related_persons, customs, notes, favorite, id } =
      data

    this.phone = phones?.[0]?.number || null

    this.phones = phones || []
    this.emails = emails || []
    this.addresses = addresses || []
    this.links = links || []
    this.dates = event_dates || []
    this.relatedPersons = related_persons || []
    this.customFields = customs || []
    this.notes = notes || []

    this.favorite = favorite || false
    this.id = id || null
  }

  initOrganizationData(data: OrganizationContactData): void {
    const {
      display_name,
      email,
      emails,
      profile_photo_url,
      is_use_default_photo,
      jobData,
      departmentData,
      mobiles,
      tels,
    } = data

    // 기본 정보 설정
    this.fullName = display_name || null
    this.email = email || null
    this.photoUrl = is_use_default_photo ? null : profile_photo_url || null

    let secondLine = ''
    if (jobData) {
      if (jobData.jobposition?.length && jobData.jobposition.length > 0) {
        secondLine += jobData.jobposition
      }
      if (jobData.jobtitle?.length && jobData.jobtitle.length > 0) {
        if (secondLine.length > 0) {
          secondLine += ' / '
        }
        secondLine += jobData.jobtitle
      }
    }
    if (departmentData?.title?.length && departmentData.title.length > 0) {
      if (secondLine.length > 0) {
        secondLine += ' • '
      }
      secondLine += departmentData.title
    }
    this.displayName = secondLine

    const allTels: PhoneData[] = []
    if (tels?.length && tels.length > 0) {
      allTels.push(...tels.map((t) => ({ type: '', number: t })))
    }
    if (mobiles?.length && mobiles.length > 0) {
      allTels.push(...mobiles.map((m) => ({ type: m.type, number: m.value })))
    }

    this.phone = allTels?.[0]?.number || null

    this.phones = allTels
    this.emails = emails?.map((e) => ({ type: e.type, email: e.address })) || []
  }

  initPersonalData(data: PersonalContactData): void {
    const { person, organization, emails, hasPhoto, photo } = data.contact

    this.personalContactData = data || null
    this.isPersonal = true

    this.displayName = person?.display_name || null
    this.nickname = person?.nickname || null
    this.email = emails?.[0]?.email || null
    this.position = organization?.job_title || null
    this.department = organization?.department || null
    this.company = organization?.company || null
    this.orgInfo = [this.position, this.department, this.company].filter(Boolean).join(' • ')
    this.hasPhoto = hasPhoto
    this.photoUrl = photo || null // VCard data 로 초기화시 적용

    this.firstName = person?.first_name || null
    this.lastName = person?.last_name || null
    this.middleName = person?.middle_name || null
    this.prefix = person?.prefix || null
    this.suffix = person?.suffix || null
    this.fullName = person?.full_name || null
    this.yomiFirstName = person?.phonetic_first_name || null
    this.yomiLastName = person?.phonetic_last_name || null
    this.yomiMiddleName = person?.phonetic_middle_name || null
    this.yomiCompany = organization?.phonetic_company || null

    this.phones = data.contact.phones || []
    this.emails = emails || []
    this.addresses = data.contact.addresses || []
    this.links = data.contact.links || []
    this.dates = data.contact.dates || []
    this.relatedPersons = data.contact.relatedPersons || []
    this.customFields = data.contact.customFields || []
    this.notes = data.contact.notes || []
    this.uid = data.contact.uid || null

    this.id = data.serverId || null
    this.etag = data.etag || null
  }

  static generateFullName(data: DetailData): string | null {
    if (data.isOrganization) return data.displayName

    const nameObj = new NameObj({
      prefix: data.prefix,
      first_name: data.firstName,
      last_name: data.lastName,
      middle_name: data.middleName,
      suffix: data.suffix,
    })
    const nameSplitter = new NameSplitter(navigator.language || 'en-US')
    nameSplitter.guessFullNameStyle(nameObj)
    return nameSplitter.joinWithName(nameObj, true, true)
  }

  static merge(data1: DetailData | null, data2: DetailData | null, info: MergeInfo): DetailData {
    const mergedData = new DetailData()
    mergedData.isMerged = true

    mergedData.displayName = data1?.displayName?.length
      ? data1.displayName
      : data2?.displayName || null
    mergedData.email = info.email || (data1?.email?.length ? data1.email : data2?.email) || null
    mergedData.photoUrl = data1?.photoUrl?.length ? data1.photoUrl : data2?.photoUrl || null
    mergedData.fullName = data1?.fullName?.length ? data1.fullName : data2?.fullName || null
    mergedData.prefix = data1?.prefix?.length ? data1.prefix : data2?.prefix || null
    mergedData.suffix = data1?.suffix?.length ? data1.suffix : data2?.suffix || null
    mergedData.firstName = data1?.firstName?.length ? data1.firstName : data2?.firstName || null
    mergedData.lastName = data1?.lastName?.length ? data1.lastName : data2?.lastName || null
    mergedData.middleName = data1?.middleName?.length ? data1.middleName : data2?.middleName || null
    mergedData.orgInfo = data1?.orgInfo?.length ? data1.orgInfo : data2?.orgInfo || null
    mergedData.company = data1?.company?.length ? data1.company : data2?.company || null
    mergedData.department = data1?.department?.length ? data1.department : data2?.department || null
    mergedData.position = data1?.position?.length ? data1.position : data2?.position || null

    mergedData.phones = (data1?.phones || []).concat(data2?.phones || [])
    mergedData.emails = (data1?.emails || []).concat(data2?.emails || [])
    mergedData.addresses = (data1?.addresses || []).concat(data2?.addresses || [])
    mergedData.links = (data1?.links || []).concat(data2?.links || [])
    mergedData.dates = (data1?.dates || []).concat(data2?.dates || [])
    mergedData.relatedPersons = (data1?.relatedPersons || []).concat(data2?.relatedPersons || [])
    mergedData.customFields = (data1?.customFields || []).concat(data2?.customFields || [])
    mergedData.notes = (data1?.notes || []).concat(data2?.notes || [])

    return mergedData
  }
}

export default DetailData
